import { CellState, queenAttacks } from '../board';
import { sigKey } from '../rules';
import { BitSet } from './bitset';
import {
  BoardCounts, ModelParams, NF, PlayerParams, RateContext,
  SigVocab, featuresOf, logRate, markSubsumed,
} from './cost';
import { FactId, factCol, factRow, isQueenFact } from './facts';
import { DeductionGraph, Support } from './graph';

// Monte-Carlo solve simulator.
//
// Walks the deduction graph the way a player walks a puzzle: at each step the live supports
// compete, one fires after an exponential wait, its fact goes on the board, and the newly
// satisfied supports come alive. Averaged over traces this gives a predicted solve time — the
// difficulty scalar the whole plan is aimed at.
//
// ── Two clocks ──
// `deductionMs` is search time only; `totalMs` adds the motor cost of actually clicking. Both are
// reported, but `totalMs` is the one difficulty is quoted on: a player reasons *while* clicking,
// and a search-time-only number is not a time anyone can recognise. It is not quoted raw either —
// see difficulty.ts, which calibrates it against observed human solve times.
//
// The simulator runs on the prebuilt graph rather than re-running the rule engine at every step.
// Support premises are minimal, so liveness is a bitmask subset test — nine AND-compares instead
// of a whole-board enumeration, which is the difference between profiling the corpus in minutes
// and in days.

export interface SimulateOptions {
  /** How many traces to average. 24 is enough for a ±5% standard error on the mean. */
  traces?: number;
  mode?: 'autoXs' | 'manual';
  /** Deterministic seed, so a published difficulty number is reproducible. */
  seed?: number;
  maxSteps?: number;
}

export interface SimulationResult {
  /** Mean search time across traces, excluding the clicking. */
  deductionMs: number;
  /** Mean wall-clock including the clicking — what difficulty is quoted on. */
  totalMs: number;
  /** Standard error of `deductionMs`, so callers can tell noise from signal. */
  deductionSeMs: number;
  /** …and of `totalMs`, which is the one difficulty is derived from. */
  totalSeMs: number;
  /** Mean number of deductions a trace needed. */
  steps: number;
  /** Fraction of traces that reached the full solution. <1 ⇒ the rules cannot finish it. */
  solvedFraction: number;
  traces: number;
}

/** xorshift32 — deterministic and fast; Math.random would make difficulty unreproducible. */
function rng(seed: number): () => number {
  let s = seed >>> 0 || 0x9e3779b9;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
}

/**
 * Candidate counts maintained incrementally. Rebuilding a full `Analysis` per simulated step
 * would cost more than every rate evaluation in the trace put together.
 */
class LiveCounts implements BoardCounts {
  readonly size: number;
  private row: Int32Array;
  private col: Int32Array;
  private region = new Map<string, number>();
  private regionTotal = new Map<string, number>();
  marked = 0;

  constructor(readonly colorRegions: string[][]) {
    this.size = colorRegions.length;
    this.row = new Int32Array(this.size).fill(this.size);
    this.col = new Int32Array(this.size).fill(this.size);
    for (let r = 0; r < this.size; r++) {
      for (let c = 0; c < this.size; c++) {
        const k = colorRegions[r][c];
        this.region.set(k, (this.region.get(k) ?? 0) + 1);
        this.regionTotal.set(k, (this.regionTotal.get(k) ?? 0) + 1);
      }
    }
  }

  /** A cell stopped being a candidate. */
  consume(r: number, c: number): void {
    this.row[r]--; this.col[c]--;
    const k = this.colorRegions[r][c];
    this.region.set(k, (this.region.get(k) ?? 0) - 1);
    this.marked++;
  }

  rowCands(i: number): number { return Math.max(0, this.row[i]); }
  colCands(i: number): number { return Math.max(0, this.col[i]); }
  regionCands(color: string): number { return Math.max(0, this.region.get(color) ?? 0); }
  regionSize(color: string): number { return this.regionTotal.get(color) ?? 0; }
}

function paramsFor(model: ModelParams, vocab: SigVocab, player: PlayerParams) {
  return { vocab, alpha: model.alpha, beta: model.beta, theta: player.theta, delta: player.delta };
}

/**
 * Simulate solving `graph`'s puzzle as `player`.
 *
 * One graph serves both modes: in `manual` a queen placement additionally costs motor time for
 * every reach cell the player has to write down, and — importantly — those facts only enter the
 * state once marked, which correctly serializes later deductions behind the bookkeeping.
 */
export function simulate(
  graph: DeductionGraph, model: ModelParams, player: PlayerParams, opts: SimulateOptions = {},
): SimulationResult {
  const traces = opts.traces ?? 24;
  const mode = opts.mode ?? 'autoXs';
  const maxSteps = opts.maxSteps ?? graph.facts.length + 64;
  const vocab = SigVocab.from(model.sigKeys);
  const P = paramsFor(model, vocab, player);
  const size = graph.size;
  const nFacts = graph.facts.length;

  // State-independent per-support data, computed once for all traces.
  const sigIds = graph.supports.map(s => vocab.idOf(sigKey(s.sig)));
  const subsumed = markSubsumed(graph.supports.map(s => ({ kind: 'x', cells: s.cells })));
  const yieldOwners = yieldIndexOf(graph);
  // The graph carries one support per (instance, fact), so a single deduction eliminating five
  // cells appears five times. The fit counts *distinct deductions* (dedupeRisk), so the
  // simulator must group the same way or `logLive` would mean two different things in the two
  // places it is used — and its coefficient is large.
  const groupOf = (() => {
    const ids = new Map<string, number>();
    return graph.supports.map(s => {
      const k = `${sigKey(s.sig)}|${s.cells.join(' ')}`;
      let id = ids.get(k);
      if (id === undefined) { id = ids.size; ids.set(k, id); }
      return id;
    });
  })();
  const nGroups = Math.max(1, ...groupOf) + 1;

  const deductionMs: number[] = [];
  const totalMs: number[] = [];
  const stepCounts: number[] = [];
  let solved = 0;

  for (let t = 0; t < traces; t++) {
    const rand = rng((opts.seed ?? 1) * 7919 + t * 104729);
    const state = new BitSet(nFacts);
    const board: CellState[][] = Array.from({ length: size }, () => Array<CellState>(size).fill(null));
    const counts = new LiveCounts(graph.colorRegions);
    const ctx: RateContext = {
      A: counts, markedCount: 0, lastCell: null, liveCount: 0,
      unitLastUsed: new Map(), sigLastUsed: new Map(), burstIndex: 0,
    };

    // A support is live when every premise is known and its fact is not. Tracked incrementally:
    // only supports indexed against a newly-known fact can change status.
    const satisfied = new Uint8Array(graph.supports.length);
    const remaining = new Int32Array(graph.supports.length);
    graph.supports.forEach((s, i) => { remaining[i] = s.premises.length; });
    const live = new Set<number>();
    for (let i = 0; i < graph.supports.length; i++) if (remaining[i] === 0) live.add(i);

    let searchMs = 0, motorMs = 0, steps = 0;
    let queens = 0;
    const x = new Float32Array(NF);
    const groupSeen = new Uint8Array(nGroups);

    const learn = (f: FactId, isBookkeeping: boolean) => {
      if (state.has(f)) return;
      state.add(f);
      const r = factRow(f, size), c = factCol(f, size);
      if (board[r][c] === null) counts.consume(r, c);
      board[r][c] = isQueenFact(f, size) ? 'queen' : 'X';
      if (isQueenFact(f, size)) queens++;
      if (isBookkeeping) motorMs += player.motor.perCellMs;
      for (const sid of graph.premiseIndex[f] ?? []) {
        if (--remaining[sid] === 0 && !satisfied[sid]) live.add(sid);
      }
    };

    // A fact landing retires every route to it at once — once you know it, no other deduction
    // for it is worth spending time on.
    const retire = (f: FactId) => {
      for (const sid of yieldOwners[f] ?? []) {
        satisfied[sid] = 1;
        live.delete(sid);
      }
    };

    const apply = (f: FactId, isBookkeeping: boolean) => {
      const known = state.has(f);
      learn(f, isBookkeeping);
      if (!known) retire(f);
    };

    // Queen reach: free under Auto-Xs, one click each under manual play.
    const closeReach = () => {
      for (let r = 0; r < size; r++) {
        for (let c = 0; c < size; c++) {
          if (board[r][c] !== 'queen') continue;
          for (const k of queenAttacks(r, c, graph.colorRegions)) {
            const [ar, ac] = k.split(',').map(Number);
            const fid = ar * size + ac;
            if (!state.has(fid)) apply(fid, mode === 'manual');
          }
        }
      }
    };

    while (steps < maxSteps && queens < size) {
      // Rates over everything currently available — the competing risks.
      let sumLam = 0;
      const ids: number[] = [];
      const lams: number[] = [];
      groupSeen.fill(0);
      let distinct = 0;
      for (const sid of live) if (!groupSeen[groupOf[sid]]) { groupSeen[groupOf[sid]] = 1; distinct++; }
      ctx.liveCount = distinct;
      for (const sid of live) {
        const s = graph.supports[sid];
        featuresOf(s, ctx, subsumed[sid] === true, x, 0);
        const z = logRate(P.alpha, P.beta, P.theta, P.delta, sigIds[sid], x, 0);
        const l = Math.exp(Math.max(-30, Math.min(20, z)));
        ids.push(sid); lams.push(l); sumLam += l;
      }
      if (!ids.length || !(sumLam > 0)) break;

      // Exponential wait, then the winner in proportion to its rate.
      searchMs += (-Math.log(1 - rand()) / sumLam) * 1000;
      let pick = rand() * sumLam, chosen = ids[ids.length - 1];
      for (let i = 0; i < ids.length; i++) {
        pick -= lams[i];
        if (pick <= 0) { chosen = ids[i]; break; }
      }

      const s: Support = graph.supports[chosen];
      // A player who spots a deduction writes down everything it concludes whose own support is
      // also live — that is what "apply all" means in the panel, and what people actually do.
      for (const f of s.yields) apply(f, false);
      for (const cellK of s.cells) {
        const [r, c] = cellK.split(',').map(Number);
        const fid = r * size + c;
        if (!state.has(fid) && isRedundantElim(graph, fid)) apply(fid, false);
      }
      motorMs += player.motor.interceptMs + player.motor.perCellMs;
      closeReach();

      ctx.burstIndex++;
      ctx.markedCount = counts.marked;
      const y = s.yields[0];
      if (y !== undefined) ctx.lastCell = [factRow(y, size), factCol(y, size)];
      ctx.sigLastUsed.set(sigKey(s.sig), ctx.burstIndex);
      for (const u of s.units) {
        ctx.unitLastUsed.set(u.type === 'region' ? `region:${u.color}` : `${u.type}:${u.index}`, ctx.burstIndex);
      }
      steps++;
    }

    if (queens === size) solved++;
    deductionMs.push(searchMs);
    totalMs.push(searchMs + motorMs);
    stepCounts.push(steps);
  }

  const mean = (a: number[]) => a.reduce((x, y) => x + y, 0) / Math.max(1, a.length);
  const se = (a: number[], m: number) => Math.sqrt(mean(a.map(v => (v - m) ** 2)) / Math.max(1, a.length));
  const md = mean(deductionMs);
  const mt = mean(totalMs);
  return {
    deductionMs: md,
    totalMs: mt,
    deductionSeMs: se(deductionMs, md),
    totalSeMs: se(totalMs, mt),
    steps: mean(stepCounts),
    solvedFraction: solved / traces,
    traces,
  };
}

/** fact → the supports that establish it. */
function yieldIndexOf(graph: DeductionGraph): number[][] {
  const out: number[][] = Array.from({ length: graph.facts.length }, () => []);
  graph.supports.forEach((s, i) => { for (const f of s.yields) out[f].push(i); });
  return out;
}

/** Does the graph know this elimination at all? Cells a support concludes but that were never
 *  derived belong to a state the closure did not reach; writing them would be unsound. */
function isRedundantElim(graph: DeductionGraph, fid: FactId): boolean {
  return graph.facts[fid]?.derived === true && graph.facts[fid].kind === 'elim';
}

/** The state a mode + player pair implies for a whole corpus profile. */
export function simulateBoth(
  graph: DeductionGraph, model: ModelParams, player: PlayerParams, opts: SimulateOptions = {},
): { autoXs: SimulationResult; manual: SimulationResult } {
  return {
    autoXs: simulate(graph, model, player, { ...opts, mode: 'autoXs' }),
    manual: simulate(graph, model, player, { ...opts, mode: 'manual' }),
  };
}
